import { useState } from 'react';
import { Check, ChevronDown, ChevronLeft, ArrowRight, Pencil, ListChecks, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export const WIZARD_STEPS = [
  { id: 1, label: 'Choose task', hint: 'What to create' },
  { id: 2, label: 'Add context', hint: 'Curriculum & source' },
  { id: 3, label: 'Review & generate', hint: 'Confirm and create' },
];

export function WizardStepper({ step, maxStep, onJump, testIdPrefix }) {
  return (
    <ol className="flex items-center gap-2 sm:gap-3 mb-7" data-testid={`${testIdPrefix}-wizard-stepper`}>
      {WIZARD_STEPS.map((s, i) => {
        const active = step === s.id;
        const done = step > s.id;
        const reachable = s.id <= maxStep;
        return (
          <li key={s.id} className={cn('flex items-center gap-2 sm:gap-3 min-w-0', i < WIZARD_STEPS.length - 1 && 'flex-1')}>
            <button
              type="button"
              disabled={!reachable}
              onClick={() => onJump(s.id)}
              aria-current={active ? 'step' : undefined}
              data-testid={`${testIdPrefix}-wizard-step-${s.id}`}
              className={cn('flex items-center gap-2.5 min-w-0 text-left', !reachable && 'opacity-50 cursor-not-allowed')}
            >
              <span className={cn(
                'h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-sm font-bold transition-[background-color,box-shadow,color] duration-300',
                active && 'bg-ae-blue text-white shadow-blue ring-4 ring-ae-blue/15',
                done && 'bg-ae-success text-white',
                !active && !done && 'bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 text-ae-slate'
              )}>
                {done ? <Check className="h-4 w-4" /> : s.id}
              </span>
              <span className={cn('min-w-0', active ? 'block' : 'hidden md:block')}>
                <span className={cn('block text-sm font-heading truncate', active ? 'font-bold text-ae-ink dark:text-white' : 'font-semibold text-ae-slate')}>{s.label}</span>
                <span className="hidden sm:block text-[11px] text-muted-foreground truncate">{s.hint}</span>
              </span>
            </button>
            {i < WIZARD_STEPS.length - 1 && (
              <span className="flex-1 h-0.5 min-w-[12px] rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                <span className={cn('block h-full bg-ae-success transition-[width] duration-500 ease-out', done ? 'w-full' : 'w-0')} />
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function WizardPanelHeading({ step, title, description }) {
  return (
    <div className="mb-5">
      <div className="ae-eyebrow text-ae-blue">Step {step} of 3</div>
      <h3 className="mt-1 text-lg sm:text-xl font-heading font-bold tracking-tight text-ae-ink dark:text-white">{title}</h3>
      {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
    </div>
  );
}

export function WizardNav({ step, onBack, onNext, canNext, nextLabel, blockedHint, testIdPrefix }) {
  return (
    <div className="mt-7 pt-5 border-t border-slate-200/80 dark:border-slate-800 flex items-center justify-between gap-3" data-testid={`${testIdPrefix}-wizard-nav`}>
      <Button type="button" variant="ghost" onClick={onBack} disabled={step === 1} data-testid={`${testIdPrefix}-wizard-back`} className="gap-1.5">
        <ChevronLeft className="h-4 w-4" /> Back
      </Button>
      {step < 3 && (
        <div className="flex items-center gap-3 min-w-0">
          {!canNext && blockedHint && (
            <span className="hidden sm:inline text-xs text-muted-foreground truncate" data-testid={`${testIdPrefix}-wizard-hint`}>{blockedHint}</span>
          )}
          <Button type="button" onClick={onNext} disabled={!canNext} data-testid={`${testIdPrefix}-wizard-next`} className="gap-1.5 px-5">
            {nextLabel} <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

export function ReviewSummary({ title, rows, onEdit, testIdPrefix }) {
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-soft overflow-hidden divide-y divide-slate-200/80 dark:divide-slate-800" data-testid={`${testIdPrefix}-review-summary`}>
      <div className="px-5 sm:px-6 py-4 bg-ae-bg/70 dark:bg-slate-800/40 flex items-center gap-3">
        <span className="h-9 w-9 rounded-xl bg-ae-navy text-ae-cyan flex items-center justify-center shrink-0">
          <ListChecks className="h-4 w-4" />
        </span>
        <div>
          <div className="text-sm font-heading font-bold text-ae-ink dark:text-white">{title}</div>
          <div className="text-[11px] text-muted-foreground">Check your choices — tap Edit to change any section.</div>
        </div>
      </div>
      {rows.map((r) => (
        <div key={r.key} className="px-5 sm:px-6 py-3.5 flex items-start sm:items-center justify-between gap-3 hover:bg-slate-50/70 dark:hover:bg-slate-800/30 transition-colors" data-testid={`${testIdPrefix}-review-row-${r.key}`}>
          <div className="min-w-0 flex-1 grid sm:grid-cols-[140px_minmax(0,1fr)] gap-0.5 sm:gap-4">
            <div className="ae-eyebrow pt-0.5">{r.label}</div>
            <div className={cn('text-sm break-words', r.value ? 'font-semibold text-ae-ink dark:text-white' : 'italic text-muted-foreground')} data-testid={`${testIdPrefix}-review-value-${r.key}`}>
              {r.value || 'Not set'}
            </div>
          </div>
          {r.step && (
            <button type="button" onClick={() => onEdit(r.step)} data-testid={`${testIdPrefix}-review-edit-${r.key}`} className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-ae-blue hover:text-ae-blue-hover hover:underline underline-offset-4">
              <Pencil className="h-3 w-3" /> Edit
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function Disclosure({ title, subtitle, icon: Icon = Settings, defaultOpen = false, testId, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900/40" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`${testId}-toggle`}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left rounded-2xl hover:bg-ae-bg/70 dark:hover:bg-slate-800/40 transition-colors"
      >
        <span className="flex items-center gap-3 min-w-0">
          <span className="h-8 w-8 rounded-lg bg-ae-blue/10 text-ae-blue flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-ae-ink dark:text-white">{title}</span>
            {subtitle && <span className="block text-[11px] text-muted-foreground truncate">{subtitle}</span>}
          </span>
        </span>
        <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform duration-200', open && 'rotate-180')} />
      </button>
      <div className={cn('px-4 pb-4', !open && 'hidden')}>{children}</div>
    </div>
  );
}

export function SelectionCard({ active, onClick, icon: Icon, title, description, testId, compact = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={cn(
        'group relative flex flex-col text-left rounded-2xl bg-white dark:bg-slate-900 transition-[border-color,box-shadow,background-color] duration-200',
        compact ? 'p-3' : 'p-4',
        active
          ? 'border-2 border-ae-blue shadow-blue ring-2 ring-ae-blue/10'
          : 'border border-slate-200/80 dark:border-slate-800 hover:border-ae-blue/40 hover:bg-ae-bg/60 dark:hover:bg-slate-800/50'
      )}
    >
      {Icon && (
        <span className={cn(
          'rounded-xl flex items-center justify-center mb-2.5 transition-transform duration-200 group-hover:scale-105',
          compact ? 'h-8 w-8' : 'h-10 w-10',
          active ? 'bg-ae-blue text-white' : 'bg-ae-bg dark:bg-slate-800 text-ae-ink dark:text-slate-200'
        )}>
          <Icon className="h-4 w-4" />
        </span>
      )}
      <span className="text-sm font-heading font-semibold leading-tight text-ae-ink dark:text-white pr-5">{title}</span>
      {description && <span className="mt-1 text-[11px] leading-snug text-muted-foreground line-clamp-2">{description}</span>}
      {active && (
        <span className="absolute top-3 right-3 h-5 w-5 rounded-full bg-ae-blue text-white flex items-center justify-center">
          <Check className="h-3 w-3" />
        </span>
      )}
    </button>
  );
}
